import { Input } from "@/components/ui/input";
import { BrandName } from "@/lib/brand";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { MultiSelect } from "@/components/apply/MultiSelect";
import { useState } from "react";
import { GENDERS, BREEDS, DISCIPLINES, CURRENCIES, currencyFor, formatPrice } from "@/lib/horseOptions";
import { COUNTRIES, REGIONS, CONTACT_PREFS } from "@/lib/geo";
import { Checkbox } from "@/components/ui/checkbox";
import { formatPhone, validPhone } from "@/lib/applyApi";
import { PapersSection } from "@/components/apply/MediaStep";
import { HorseTrainerFields, validateTrainer } from "@/components/apply/TrainerFields";
import { PromoConsent } from "@/components/apply/PromoConsent";
import { RegistrationConfirm } from "@/components/apply/RegistrationConfirm";
import { latestScan } from "@/lib/regMap";
import { Lock } from "lucide-react";
import { HealthBehaviour, validateHealth } from "@/components/apply/HealthFields";

export function YesNo({ id, label, value, onChange, error }) {
  return (
    <Field id={id} label={label} required error={error}>
      <div role="radiogroup" aria-labelledby={`${id}-label`} aria-invalid={!!error} className="grid grid-cols-2 gap-2 max-w-xs">
        {[["yes", "Yes"], ["no", "No"]].map(([k, l]) => (
          <button key={k} type="button" role="radio" aria-checked={value === k} data-testid={`${id}-${k}`} onClick={() => onChange(k)}
            className={`h-12 rounded-lg border text-sm font-semibold focus:outline-none focus:ring-2 ${error ? "focus:ring-red-500" : "focus:ring-amber-700"} ${value === k ? "bg-amber-800 border-amber-800 text-white" : `bg-white ${error ? "border-red-400" : "border-stone-300"} text-stone-700`}`}>{l}</button>
        ))}
      </div>
    </Field>
  );
}

export function AddressFields({ v, set, errors = {}, prefix = "apply" }) {
  const f = (k) => (x) => set({ ...v, [k]: x });
  return (
    <div className="space-y-4">
      <TextField id={`${prefix}-street`} label="Street Address / PO Box / Rural Route" required value={v.street_address} onChange={f("street_address")} error={errors.street_address} autoComplete="street-address" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextField id={`${prefix}-city`} label="City / Town" required value={v.city} onChange={f("city")} error={errors.city} autoComplete="address-level2" />
        <TextField id={`${prefix}-postal`} label={v.country === "US" ? "ZIP Code" : "Postal Code / ZIP Code"} required value={v.postal_code} onChange={f("postal_code")} error={errors.postal_code} autoComplete="postal-code" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Dropdown id={`${prefix}-country`} label="Country" required value={v.country} options={COUNTRIES} placeholder="Select" error={errors.country}
          onChange={(x) => set({ ...v, country: x, region: "" })} />
        <Dropdown key={v.country || "none"} id={`${prefix}-region`} required label={v.country === "US" ? "State" : "Province / State"} value={v.region} error={errors.region}
          options={REGIONS[v.country] || []} placeholder={v.country ? "Select" : "Pick country first"} onChange={f("region")} />
      </div>
    </div>
  );
}

export function ContactPreference({ value, consent, onChange, errors = {}, promo, application }) {
  const wantsSms = value === "sms" || value === "both";
  // Application mode: choosing Text/Both is the consent for application texts. Optional promotional consent is asked only on Review.
  const appMode = promo || application;
  const pick = (k) => onChange({ contact_preference: k, sms_consent: appMode ? k !== "email" : k === "email" ? false : consent });
  const err = errors.contact_preference;
  return (
    <Field id="apply-contact-pref" label="How would you prefer us to contact you?" required invalid={!!err}>
      <div role="radiogroup" aria-labelledby="apply-contact-pref-label" aria-invalid={!!err} aria-describedby={err ? "apply-contact-pref-error" : undefined}
        className="grid grid-cols-3 gap-2" onKeyDown={(e) => radioKeys(e, value, pick)}>
        {CONTACT_PREFS.map(([k, l], i) => (
          <button key={k} type="button" role="radio" aria-checked={value === k} data-testid={`contact-pref-${k}`}
            tabIndex={value === k || (!value && i === 0) ? 0 : -1}
            onClick={() => pick(k)}
            className={`h-12 rounded-lg border text-sm font-semibold transition-colors focus:outline-none focus:ring-2 ${err ? "focus:ring-red-500" : "focus:ring-amber-700"} ${value === k ? "bg-amber-800 border-amber-800 text-white" : `bg-white ${err ? "border-red-400" : "border-stone-300"} text-stone-700`}`}>
            {l}
          </button>
        ))}
      </div>
      {err && <p id="apply-contact-pref-error" data-testid="apply-contact-pref-error" className="mt-1 text-xs font-medium text-red-600">{err}</p>}
      {promo ? (
        <label className="mt-3 flex items-start gap-3 min-h-[44px] cursor-pointer">
          <Checkbox data-testid="promo-consent" checked={!!promo.checked} onCheckedChange={(v) => promo.onChange(!!v)} className="mt-0.5 h-5 w-5" />
          <span className="text-sm text-stone-700">I agree to receive application updates, horse sale announcements, newsletters, and promotional messages from <BrandName /> by email or text message, based on my contact preferences. I can unsubscribe from promotional messages at any time.</span>
        </label>
      ) : wantsSms && !application && (
        <label className="mt-3 flex items-start gap-3 min-h-[44px] cursor-pointer">
          <Checkbox data-testid="sms-consent" checked={!!consent} onCheckedChange={(v) => onChange({ contact_preference: value, sms_consent: !!v })} className="mt-0.5 h-5 w-5" />
          <span className="text-sm text-stone-700">I agree to receive text message notifications about my application at the phone number above. Message &amp; data rates may apply. Reply STOP to opt out at any time.</span>
        </label>
      )}
      {errors.sms_consent && <p data-testid="sms-consent-error" className="mt-1 text-xs font-medium text-red-600">{errors.sms_consent}</p>}
    </Field>
  );
}

function radioKeys(e, value, pick) {
  const keys = CONTACT_PREFS.map(([k]) => k);
  const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
  if (!step) return;
  e.preventDefault();
  const i = Math.max(0, keys.indexOf(value));
  const k = keys[(i + step + keys.length) % keys.length];
  pick(k);
  e.currentTarget.querySelector(`[data-testid="contact-pref-${k}"]`)?.focus();
}

export function Field({ id, label, required, error, hint, children, invalid }) {
  return (
    <div data-field-invalid={error || invalid ? "true" : undefined}
      className={`min-w-0 rounded-lg ${error || invalid ? "outline outline-2 outline-red-300 outline-offset-4" : ""}`}>
      <label id={`${id}-label`} htmlFor={id} className="block text-sm font-semibold text-stone-800 mb-1.5">
        {label} {required && <span className="text-amber-800">*</span>}
      </label>
      {children}
      {hint && !error && <p className="mt-1 text-xs text-stone-500">{hint}</p>}
      {error && <p data-testid={`${id}-error`} className="mt-1 text-xs font-medium text-red-600">{error}</p>}
    </div>
  );
}

const inputCls = "h-12 text-base bg-white border-stone-300 focus-visible:ring-amber-800";

export function TextField({ id, label, value, onChange, required, error, type = "text", hint, ...rest }) {
  return (
    <Field id={id} label={label} required={required} error={error} hint={hint}>
      <Input id={id} data-testid={id} type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} aria-invalid={!!error}
        className={`${inputCls} ${error ? "border-red-400 focus-visible:ring-red-500" : ""}`} {...rest} />
    </Field>
  );
}

export function StepContact({ contact, set, errors, promoExisting, owner = false }) {
  const f = (k) => (v) => set({ ...contact, [k]: v });
  const p = owner ? "Owner " : "";
  return (
    <div className="space-y-4">
      <TextField id="apply-full-name" label={`${p}Full Name`} required value={contact.full_name} onChange={f("full_name")} error={errors.full_name} autoComplete={owner ? "off" : "name"} />
      <TextField id="apply-email" label={`${p}Email Address`} required type="email" value={contact.email} onChange={f("email")} error={errors.email} autoComplete={owner ? "off" : "email"} inputMode="email" hint={owner ? "The contract is sent here for the owner to sign" : undefined} />
      <TextField id="apply-phone" label={`${p}Phone Number`} required type="tel" value={contact.phone} onChange={f("phone")} error={errors.phone} autoComplete={owner ? "off" : "tel"} inputMode="tel" hint="Include area code, e.g. +1 403 555 0123"
        onBlur={() => set({ ...contact, phone: formatPhone(contact.phone) })} />
      {owner && <p data-testid="apply-owner-address-note" className="text-xs font-semibold uppercase tracking-wide text-stone-500 pt-2">Owner's mailing address and how the owner prefers to be contacted</p>}
      <AddressFields v={contact} set={set} errors={errors} />
      <ContactPreference value={contact.contact_preference} consent={contact.sms_consent} errors={errors}
        onChange={(p) => set({ ...contact, ...p })} application />
      <PromoConsent contact={contact} existing={promoExisting} onChange={(v) => set({ ...contact, promo_consent: v, promo_consent_method: "user" })} />
    </div>
  );
}

function Dropdown({ id, label, value, onChange, options, placeholder, error, required }) {
  return (
    <Field id={id} label={label} error={error} required={required}>
      <Select value={value || undefined} onValueChange={onChange}>
        <SelectTrigger id={id} data-testid={id} aria-labelledby={`${id}-label`} aria-invalid={!!error} className={`h-12 text-base bg-white focus:ring-amber-800 ${error ? "border-red-400 focus:ring-red-500" : "border-stone-300"}`}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="max-h-[50vh]">
          {options.map(([k, l]) => (
            <SelectItem key={k} value={k} data-testid={`${id}-option-${k}`} className="min-h-[44px] text-base">{l}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}

const paperIds = (app) => (app?.media || []).filter((m) => m.kind === "registration_paper").map((m) => m.id).sort().join(",");
export const regConfirmed = (horse, app) => !!horse.registration_confirmed_at && !!paperIds(app) && horse.registration_confirmed_papers === paperIds(app);

export function StepHorse({ horse, set, errors, app, token, company, onMediaChange, saveHorse, contact, dupNotice }) {
  const f = (k) => (v) => set({ ...horse, [k]: v });
  const papers = (app?.media || []).filter((m) => m.kind === "registration_paper");
  const scan = latestScan(papers);
  const [manual, setManual] = useState(false);
  const [editing, setEditing] = useState(false);
  const confirmed = regConfirmed(horse, app);
  const scanned = papers.length > 0 && papers.every((m) => m.scan);
  const showPanel = horse.registered === "yes" && papers.length > 0 && (!confirmed || editing) && (scan || manual);
  const confirm = (vals) => {
    const v = { ...horse, ...vals, registered: "yes", registration_source: scan ? "ai" : "manual", registration_confirmed_at: new Date().toISOString(), registration_confirmed_papers: paperIds(app) };
    set(v); setEditing(false); saveHorse?.(v);
  };
  const showRest = horse.registered === "no" || (horse.registered === "yes" && confirmed && !editing);
  return (
    <div className="space-y-4">
      <YesNo id="apply-registered" label="Is your horse registered?" value={horse.registered} error={errors.registered}
        onChange={(v) => set({ ...horse, registered: v, ...(v === "no" ? { registered_name: "", registered_number: "" } : {}) })} />
      {horse.registered === "yes" && app && token && <PapersSection app={app} token={token} company={company} onMediaChange={onMediaChange} onManual={() => setManual(true)} />}
      {horse.registered === "yes" && !papers.length && <p data-testid="reg-gate-upload" className="text-sm text-stone-600">Upload at least one registration paper to continue.</p>}
      {horse.registered === "yes" && papers.length > 0 && !scanned && !confirmed && <p data-testid="reg-gate-scanning" className="text-sm text-stone-600">Reading your registration papers… this usually takes a few seconds.</p>}
      {dupNotice}
      {showPanel && <RegistrationConfirm key={(scan?.scanned_at || "manual") + paperIds(app)} scan={scan} horse={horse} onConfirm={confirm} errors={errors} />}
      {horse.registered === "yes" && confirmed && !editing && (
        <div data-testid="reg-confirmed" className="rounded-xl border border-emerald-300 bg-emerald-50 p-3 flex items-center justify-between gap-2 text-sm text-emerald-900">
          <span>Registration information confirmed{horse.registration_source === "manual" ? " (entered manually)" : ""}.</span>
          <button type="button" data-testid="reg-correct-btn" onClick={() => setEditing(true)} className="font-semibold underline min-h-[44px] text-left">Correct Registration Information</button>
        </div>
      )}
      {errors.registration_confirm && <p data-testid="reg-gate-error" role="alert" className="text-sm font-medium text-red-600">{errors.registration_confirm}</p>}
      {showRest && <HorseRest horse={horse} f={f} errors={errors} set={set} saveHorse={saveHorse} contact={contact} trainerAsked={app?.submitter?.role === "owner"} />}
    </div>
  );
}

function LockedRegistration({ horse }) {
  return (
    <div data-testid="reg-locked" className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {[["registered_name", "Registered Name"], ["registered_number", "Registration Number"]].map(([k, l]) => (
        <Field key={k} id={`apply-locked-${k}`} label={l} hint="Locked after confirmation. Use “Correct Registration Information” above to change it.">
          <div id={`apply-locked-${k}`} data-testid={`apply-locked-${k}`} aria-readonly="true" className="h-12 px-3 flex items-center gap-2 rounded-md border border-stone-200 bg-stone-100 text-base text-stone-800">
            <Lock className="h-4 w-4 text-stone-500 shrink-0" /><span className="truncate">{horse[k]}</span>
          </div>
        </Field>
      ))}
    </div>
  );
}

function SameAsSeller({ horse, set, contact }) {
  if (!contact) return null;
  const toggle = (on) => {
    if (!on) { set({ ...horse, location_same_as_seller: false }); return; }
    const next = { location_country: contact.country || "", location_region: contact.region || "", location_city: contact.city || "" };
    const typed = ["location_country", "location_region", "location_city"].some((k) => horse[k] && horse[k] !== next[k]);
    if (typed && !window.confirm("Replace the horse location you already entered with your seller address?")) return;
    set({ ...horse, ...next, location_same_as_seller: true, ...(horse.currency_manual || !next.location_country ? {} : { price_currency: currencyFor(next.location_country) }) });
  };
  return (
    <label className="flex items-center gap-3 min-h-[44px] cursor-pointer">
      <Checkbox data-testid="horse-location-same-as-seller" checked={!!horse.location_same_as_seller} onCheckedChange={(v) => toggle(!!v)} className="h-5 w-5" />
      <span className="text-sm text-stone-700">Same as Seller Address</span>
    </label>
  );
}

function HorseRest({ horse, f, errors, set, saveHorse, contact, trainerAsked }) {
  return (
    <div className="space-y-4">
      {horse.registered === "yes" && <LockedRegistration horse={horse} />}
      <TextField id="apply-horse-name" label="Horse's Barn Name" required value={horse.horse_name} onChange={f("horse_name")} error={errors.horse_name}
        hint="The everyday name you call your horse. It doesn't need to match the registered name." />
      <div className="grid grid-cols-2 gap-4">
        <TextField id="apply-age" label="Age" value={horse.age} onChange={f("age")} placeholder="e.g. 6 or 2019" />
        <Dropdown id="apply-gender" label="Gender" value={horse.gender} onChange={f("gender")} options={GENDERS} placeholder="Select" />
      </div>
      <Dropdown id="apply-breed" label="Breed" value={horse.breed} onChange={f("breed")} options={BREEDS} placeholder="Select breed" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextField id="apply-sire" label="Sire" value={horse.sire} onChange={f("sire")} />
        <TextField id="apply-dam" label="Dam" value={horse.dam} onChange={f("dam")} />
      </div>
      {horse.breed === "other" && (
        <TextField id="apply-breed-other" label="Specify breed" required value={horse.breed_other} onChange={f("breed_other")} error={errors.breed_other} />
      )}
      {!trainerAsked && <HorseTrainerFields horse={horse} set={set} contact={contact} errors={errors} />}
      <Field id="apply-disciplines" label="Primary Discipline" hint="Select all that apply.">
        <MultiSelect id="apply-disciplines" title="Primary Discipline" options={DISCIPLINES} onDone={() => saveHorse?.(horse)} value={horse.disciplines || []}
          onChange={f("disciplines")} placeholder="Select disciplines" />
      </Field>
      {(horse.disciplines || []).includes("other") && (
        <TextField id="apply-discipline-other" label="Other discipline" required value={horse.discipline_other} onChange={f("discipline_other")} error={errors.discipline_other} />
      )}
      <Field id="apply-description" label="Brief Horse Description" required error={errors.description} hint="A few sentences: temperament, training, what they're good at.">
        <Textarea id="apply-description" data-testid="apply-description" rows={5} value={horse.description || ""}
          onChange={(e) => f("description")(e.target.value)}
          className={`text-base bg-white border-stone-300 focus-visible:ring-amber-800 ${errors.description ? "border-red-400" : ""}`} />
      </Field>
      <HealthBehaviour horse={horse} set={set} errors={errors} />
      <div className="rounded-xl border border-stone-200 bg-white/60 p-3 space-y-3">
        <div className="text-sm font-semibold text-stone-800">Location of Horse</div>
        <SameAsSeller horse={horse} set={set} contact={contact} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Dropdown id="apply-location-country" label="Country" value={horse.location_country} options={COUNTRIES} placeholder="Select"
            onChange={(v) => set({ ...horse, location_country: v, location_region: "", ...(horse.currency_manual ? {} : { price_currency: currencyFor(v) }) })} />
          <Dropdown key={horse.location_country || "none"} id="apply-location-region" label={horse.location_country === "US" ? "State" : "Province / State"}
            value={horse.location_region} options={REGIONS[horse.location_country] || []} placeholder={horse.location_country ? "Select" : "Pick country first"}
            onChange={f("location_region")} />
        </div>
        <TextField id="apply-location-city" label="City / Town" value={horse.location_city} onChange={f("location_city")} />
      </div>
      <PriceField horse={horse} set={set} error={errors.expected_price} currencyError={errors.price_currency} />
    </div>
  );
}

function PriceField({ horse, set, error, currencyError }) {
  const [focused, setFocused] = useState(false);
  const [warn, setWarn] = useState("");
  const n = horse.expected_price;
  const cur = horse.price_currency || currencyFor(horse.location_country);
  const shown = n === null || n === undefined ? "" : focused ? String(n) : Number(n).toLocaleString("en-US");
  const onChange = (raw) => {
    const s = raw.replace(/,/g, "").trim();
    if (/\D/.test(s)) { setWarn("Numbers only — no letters, symbols or minus signs (e.g. 40000)."); return; }
    if (s.length > 9) return;
    setWarn("");
    set({ ...horse, expected_price: s === "" ? null : Number(s), expected_price_legacy: s === "" ? horse.expected_price_legacy : null, price_currency: cur });
  };
  const hint = horse.expected_price_legacy && n == null ? `Previously entered: "${horse.expected_price_legacy}" — please enter it as a number.` : n != null ? `${formatPrice(n)} ${cur}` : "Whole dollars, e.g. 40000";
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[1fr_220px] gap-3 sm:gap-4 items-start">
      <Field id="apply-price" label="Expected Selling Price" required error={warn || error} hint={hint}>
        <div className="relative">
          <span aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500 text-base">$</span>
          <Input id="apply-price" data-testid="apply-price" type="text" inputMode="numeric" pattern="[0-9]*" autoComplete="off"
            value={shown} placeholder="40,000" aria-describedby="apply-price-display"
            onFocus={() => setFocused(true)} onBlur={() => { setFocused(false); setWarn(""); }}
            onChange={(e) => onChange(e.target.value)}
            className={`${inputCls} pl-7 ${warn || error ? "border-red-400" : ""}`} />
        </div>
        <span id="apply-price-display" data-testid="apply-price-display" className="sr-only">{hint}</span>
      </Field>
      <Dropdown id="apply-currency" label="Currency of Sale *" value={cur} options={CURRENCIES} error={currencyError}
        onChange={(v) => set({ ...horse, price_currency: v, currency_manual: true })} />
    </div>
  );
}

export function validateContact(c, EMAIL_RE, validPhone) {
  const e = {};
  if (!c.full_name?.trim()) e.full_name = "Please enter your full name.";
  if (!EMAIL_RE.test(c.email?.trim() || "")) e.email = "Please enter a valid email address.";
  if (!validPhone(c.phone)) e.phone = "Please enter a valid phone number (10–15 digits).";
  if (!c.contact_preference) e.contact_preference = "Please choose how you'd like us to contact you.";
  Object.assign(e, validateAddress(c));
  return e;
}

export function validateAddress(c) {
  const e = {};
  if (!c.street_address?.trim()) e.street_address = "Please enter your street address, PO Box or rural route.";
  if (!c.city?.trim()) e.city = "Please enter your city or town.";
  if (!c.postal_code?.trim()) e.postal_code = "Please enter your postal or ZIP code.";
  if (!c.country) e.country = "Please choose your country.";
  if (!c.region) e.region = "Please choose your province or state.";
  return e;
}

export function validateRegistration(h) {
  const e = {};
  if (h.registered !== "yes" && h.registered !== "no") e.registered = "Please tell us whether the horse is registered.";
  if (h.registered === "yes" && !h.registered_name?.trim()) e.registered_name = "Please enter the registered name.";
  if (h.registered === "yes" && !h.registered_number?.trim()) e.registered_number = "Please enter the registered number.";
  return e;
}

export function validateHorse(h, app) {
  const e = {};
  if (h.registered === "yes" && app && !regConfirmed(h, app)) e.registration_confirm = "Please upload your registration papers and confirm your horse's information before continuing.";
  if (!h.horse_name?.trim()) e.horse_name = "Please enter the horse's barn name.";
  // Owners answer the trainer questions on the contact step.
  Object.assign(e, validateRegistration(h), app?.submitter?.role === "owner" ? {} : validateTrainer(h, validPhone));
  if (!h.description?.trim()) e.description = "Please add a brief description.";
  Object.assign(e, validateHealth(h));
  if (h.breed === "other" && !h.breed_other?.trim()) e.breed_other = "Please specify the breed.";
  if ((h.disciplines || []).includes("other") && !h.discipline_other?.trim()) e.discipline_other = "Please enter the other discipline.";
  const p = h.expected_price;
  if (p === null || p === undefined || p === "") e.expected_price = "Expected Sale Price is required.";
  else if (!(Number.isInteger(p) && p >= 0)) e.expected_price = "Please enter the price as a whole number, e.g. 40000.";
  if (!["CAD", "USD"].includes(h.price_currency)) e.price_currency = "Please choose the Currency of Sale (CAD or USD).";
  return e;
}
